import { useState, useEffect, useCallback } from "react";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import DeleteIcon from "@styles/all-images/icons/deleteblue.svg";
import EditIcon from "@styles/all-images/icons/edit-icon-blue.svg";
import ToDoAdd from "./ToDoAdd";
import ToDoDeleteTask from "@/pages/ToDo/ToDoDeleteTask";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import { toast } from "react-toastify";
import ToDoView from "@/pages/ToDo/ToDoView";
import CustomTable from "@/components/common/table/CustomTable";
import dateHandler from "@/composables/dateUtil";
import ElipsedText from "@/components/common/elipsis/ElipsedText";
import { DocumentApprovalStatus } from "@/constants/enums/document-approval-status.enum";
import { usePermissionState } from "@/providers/PermissionProvider";
import { TodoPermissions } from "@/constants/permission-modules/base-permissions";

interface TodoData {
  id: number;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: number;
  status_text: string;
}

const TierConfigurationModule = () => {
  const [editData, setEditData] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState<TodoData | null>(null);
  const { isPermitted } = usePermissionState();

  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDate } = dateHandler();

  useEffect(() => {
    if (forceRefresh) {
      setForceRefresh(false); // Just reset the flag, CustomTable will handle the refresh
    }
  }, [forceRefresh]);

  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);

  useEffect(() => {
    // Hide body scroll when modal is open
    if (isStaffModalOpen || isViewModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    // Cleanup effect
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isStaffModalOpen, isViewModalOpen]);

  const handleOpenStaffModal = () => {
    setEditData(null);
    setIsStaffModalOpen(true);
  };

  const handleCloseStaffModal = () => {
    setIsStaffModalOpen(false);
  };

  const handleStaffSubmit = () => {
    setForceRefresh(true);
    setIsStaffModalOpen(false);
  };

  const handleOpenRejectionModal = (id: number) => {
    setSelectedTaskId(id);
    setIsRejectionModalOpen(true);
  };

  const handleCloseRejectionModal = () => {
    setIsRejectionModalOpen(false);
  };

  const handleRejectionSubmit = () => {
    setForceRefresh(true);
    setIsRejectionModalOpen(false);
  };

  const handleEdit = async (id: number) => {
    try {
      const { authenticatedCall } = actionsHandler("todoById");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [id + "/"],
        }),
      );

      if (response.ok) {
        setEditData(response.data);
        setIsStaffModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching task details:", error);
      toast.error("Unable to fetch task details");
    }
  };

  const handleView = async (id: number) => {
    try {
      const { authenticatedCall } = actionsHandler("todoById");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [id + "/"],
        }),
      );

      if (response.ok) {
        setViewData(response.data);
        setIsViewModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching task details:", error);
    }
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewData(null);
  };

  const handleViewSubmit = () => {
    setForceRefresh(true);
    setIsViewModalOpen(false);
  };
  const handleFetch = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      try {
        const { getAllData } = actionsHandler("todo");
        const response = await dispatch(
          getAllData({
            limit: params.limit || 10,
            page: params.page || 1,
            search: params.search || "",
            customQuery: new URLSearchParams({
              ...(params.sortColumn && {
                ordering:
                  (params.sortDirection === "asc" ? "" : "-") +
                  params.sortColumn,
              }),
            }).toString(),
          }),
        );

        return response;
      } catch (error) {
        console.error("Error fetching tasks:", error);
        return {
          data: {
            data: [],
            meta: {
              count: 0,
              page: 1,
              limit: 10,
            },
          },
        };
      }
    },
    [dispatch],
  );
  // Define columns for CustomTable
  const columns = [
    {
      header: "Task Name",
      accessor: (row: TodoData) => row.name,
      sortable: true,
      sortKey: "name",
    },
    {
      header: "Description",
      accessor: (row: TodoData) => {
        const description = row.description || "";
        return description.length > 200 ? (
          <ElipsedText
            text={description}
            shouldWrap={true}
            showPreferenceButtons={true}
            maxLength={200}
          />
        ) : (
          description
        );
      },
      sortable: false,
    },
    {
      header: "Status",
      accessor: (row: TodoData) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          <li
            className={`tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-text-sm tw-w-fit ${
              row.status === 1
                ? "tw-bg-[#F5F5F5] tw-text-[#414651]"
                : "tw-bg-[#ECFDF3] tw-text-[#027A48]"
            }`}
          >
            <span
              className={`tw-w-2 tw-h-2 tw-rounded-full ${
                row.status === 1 ? "tw-bg-[#717680]" : "tw-bg-[#12B76A]"
              }`}
            ></span>
            {row.status_text}
          </li>
        </div>
      ),
      sortable: true,
      sortKey: "status",
    },
    {
      header: "Start Date",
      // accessor: (row: TodoData) => row.start_date,
      accessor: (row: TodoData) => formatDate(row.start_date),
      sortable: true,
      sortKey: "start_date",
    },
    {
      header: "End Date",
      accessor: (row: TodoData) =>
        `${row.end_date ? formatDate(row.end_date) : "-"}`,
      sortable: true,
      sortKey: "end_date",
    },
    {
      header: "Actions",
      accessor: (row: TodoData) => (
        <div className="tw-flex tw-items-center">
          <button onClick={() => handleView(row.id)} title="View">
            <img
              src={EyeIcon}
              alt="View Icon"
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
            />
          </button>
          {isPermitted(TodoPermissions.DELETE_TO_DO) && (
            <button
              onClick={() => handleOpenRejectionModal(row.id)}
              title="Delete"
            >
              <img
                src={DeleteIcon}
                alt="Delete Icon"
                className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
              />
            </button>
          )}
          {isPermitted(TodoPermissions.UPDATE_TO_DO) &&
            row.status === DocumentApprovalStatus.pending && (
              <button onClick={() => handleEdit(row.id)} title="Edit">
                <img
                  src={EditIcon}
                  alt="Edit Icon"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            )}
        </div>
      ),
    },
  ];

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a href="/menu">
                <img src={BackIcon} alt="Back Icon" />
              </a>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                To Do
              </h1>
            </div>
            <div>
              {isPermitted(TodoPermissions.CREATE_TO_DO) && (
                <button
                  onClick={handleOpenStaffModal}
                  className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
                >
                  <img
                    src={PlusIcon}
                    alt="Plus Icon"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                  Add Task
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tw-p-6 tw-mt-[140px]">
        <CustomTable
          columns={columns}
          fetchData={handleFetch}
          forceRefresh={forceRefresh}
          setForceRefresh={setForceRefresh}
          noDataMessage={`No task found`}
          noSearchDataMessage={`No matching task found`}
        />

        <div>
          {isRejectionModalOpen && selectedTaskId && (
            <ToDoDeleteTask
              onClose={handleCloseRejectionModal}
              onSubmit={handleRejectionSubmit}
              taskId={selectedTaskId}
            />
          )}
        </div>

        <div
          className={`tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-full tw-bg-black/25 tw-shadow-lg tw-transition-transform tw-duration-300 tw-ease-in-out ${
            isStaffModalOpen
              ? "tw-transform tw-translate-x-0"
              : "tw-transform tw-translate-x-full"
          } tw-z-50`}
        >
          {isStaffModalOpen && (
            <ToDoAdd
              onClose={handleCloseStaffModal}
              onSubmit={handleStaffSubmit}
              isStaffModalOpen={isStaffModalOpen}
              editData={editData}
            />
          )}
        </div>
        <div
          className={`tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-full tw-bg-black/25 tw-shadow-lg tw-transition-transform tw-duration-300 tw-ease-in-out ${
            isViewModalOpen
              ? "tw-transform tw-translate-x-0"
              : "tw-transform tw-translate-x-full"
          } tw-z-50`}
        >
          {isViewModalOpen && viewData && (
            <ToDoView
              onClose={handleCloseViewModal}
              onSubmit={handleViewSubmit}
              isStaffModalOpen={isViewModalOpen}
              viewData={viewData}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TierConfigurationModule;
